import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from "@material-ui/core";
import {
  Edit,
  Delete,
  Add,
  Visibility,
  Search,
  Publish,
  Lock,
  Description,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import { useHelpArticles, useHelpCategories } from "../../hooks/useHelp";
import { deleteHelpArticle } from "../../services/helpApi";
import ConfirmationModal from "../../components/ConfirmationModal";
import HelpArticleModal from "../../components/HelpArticleModal";
import toastUtils from "../../utils/toastUtils";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(1),
  },
  statusPublished: {
    backgroundColor: theme.palette.success.main,
    color: "#fff",
  },
  statusDraft: {
    backgroundColor: theme.palette.grey[500],
    color: "#fff",
  },
}));

const HelpAdmin = ({ standalone = true }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [searchParam, setSearchParam] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [deletingArticle, setDeletingArticle] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { articles, loading } = useHelpArticles({
    searchParam,
    status: "", // String vazia para buscar todos os status
    pageNumber: 1,
  });

  const { categories } = useHelpCategories({
    searchParam: "",
    parentId: null,
  });

  const handleOpenArticleModal = () => {
    setSelectedArticle(null);
    setArticleModalOpen(true);
  };

  const handleCloseArticleModal = () => {
    setSelectedArticle(null);
    setArticleModalOpen(false);
  };

  const handleEditArticle = (article) => {
    setSelectedArticle(article);
    setArticleModalOpen(true);
  };

  const handleDeleteArticle = async (articleId) => {
    try {
      await deleteHelpArticle(articleId);
      toastUtils.success(i18n.t("helpAdmin.articleDeleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingArticle(null);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.namePt : "-";
  };

  // Verificar se é admin
  if (user?.profile !== "admin") {
    return (
      <MainContainer>
        <MainHeader>
          <Title>{i18n.t("helpAdmin.title")}</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
          >
            <Lock style={{ fontSize: 64, color: "#999", marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary">
              Acesso Restrito
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Apenas administradores podem acessar esta página
            </Typography>
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  const content = (
    <>
      <ConfirmationModal
        title={i18n.t("helpAdmin.confirmDeleteTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteArticle(deletingArticle)}
      >
        {i18n.t("helpAdmin.confirmDeleteMessage")}
      </ConfirmationModal>

      <HelpArticleModal
        open={articleModalOpen}
        onClose={handleCloseArticleModal}
        articleId={selectedArticle?.id}
        categories={categories}
      />

      {standalone && (
        <MainHeader>
          <Title>{i18n.t("helpAdmin.title")}</Title>
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenArticleModal}
              startIcon={<Add />}
            >
              {i18n.t("helpAdmin.newArticle")}
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
      )}

      {!standalone && (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{i18n.t("helpAdmin.title")}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenArticleModal}
            startIcon={<Add />}
          >
            {i18n.t("helpAdmin.newArticle")}
          </Button>
        </Box>
      )}

      <Paper className={classes.mainPaper} variant="outlined">
        <TextField
          className={classes.searchField}
          placeholder={i18n.t("helpAdmin.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="disabled" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("helpAdmin.table.title")}</TableCell>
              <TableCell>{i18n.t("helpAdmin.table.category")}</TableCell>
              <TableCell align="center">{i18n.t("helpAdmin.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("helpAdmin.table.views")}</TableCell>
              <TableCell align="center">{i18n.t("helpAdmin.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Carregando...
                </TableCell>
              </TableRow>
            )}
            {!loading && articles.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum artigo encontrado
                </TableCell>
              </TableRow>
            )}
            {!loading && articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell>{article.titlePt}</TableCell>
                <TableCell>{getCategoryName(article.categoryId)}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={article.status === "published" ? "Publicado" : "Rascunho"}
                    className={
                      article.status === "published"
                        ? classes.statusPublished
                        : classes.statusDraft
                    }
                    icon={article.status === "published" ? <Publish /> : <Description />}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={article.views}
                    icon={<Visibility />}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={i18n.t("helpAdmin.buttons.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditArticle(article)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("helpAdmin.buttons.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingArticle(article.id);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );

  return standalone ? <MainContainer>{content}</MainContainer> : content;
};

export default HelpAdmin;
